import { ThemeIcon, useMantineTheme } from '@mantine/core';
import { useMemo } from 'react';

import { useCompanyFeatureFlags } from 'hooks/use-company-feature-flags';

import { CitationCard } from './citation-card';
import {
  getCitationIcon,
  mapCitationSourceToCardData,
  resolveSourceType,
} from './citation-card/CitationCard.utils';
import CitationPopover from './CitationPopover';
import { useMarkdownRendererContext } from './markdown-renderer/MarkdownRenderer.context';

import {
  isCitationSourceEmpty,
  isCitationSourceFetched,
} from '../utils/sources.utils';

import type { CitationSource } from '../types';

type InlineCitationProps = {
  source: CitationSource | { url: string };
  onRemove?: () => void;
  removeDisabled?: boolean;
};

function DefaultInlineContent({
  source,
  onRemove,
  removeDisabled,
}: {
  source: CitationSource;
  onRemove?: () => void;
  removeDisabled?: boolean;
}) {
  const cardData = useMemo(() => {
    const { authorId: _, ...data } = mapCitationSourceToCardData(source);
    return data;
  }, [source]);

  return (
    <CitationCard
      {...cardData}
      onRemove={onRemove}
      removeDisabled={removeDisabled}
    />
  );
}

export default function InlineCitation({
  source,
  onRemove,
  removeDisabled = false,
}: InlineCitationProps) {
  const { renderCitationContentOverride } = useMarkdownRendererContext();
  const { primaryColor } = useMantineTheme();
  const { isHatchDebugCitationsEnabled } = useCompanyFeatureFlags(
    'isHatchDebugCitationsEnabled',
  );

  const sourceType =
    'citation_data' in source
      ? resolveSourceType(
          source.citation_data.source_name ?? source.citation_data.source_type,
          source.url,
        )
      : resolveSourceType(undefined, source.url);

  const Icon = getCitationIcon(sourceType);

  const isFetched = isCitationSourceFetched(source);
  const isEmpty = isCitationSourceEmpty(source);

  const iconColor =
    isFetched && isEmpty && isHatchDebugCitationsEnabled
      ? 'orange'
      : primaryColor;

  if (isFetched && isEmpty && !isHatchDebugCitationsEnabled) {
    return null;
  }

  const iconElement = (
    <ThemeIcon
      variant="light"
      color={iconColor}
      size={18}
      style={{
        '--ti-radius': '4px',
        cursor: 'pointer',
        verticalAlign: 'text-bottom',
      }}
      display="inline-flex"
    >
      <Icon size={14} />
    </ThemeIcon>
  );

  const hasCitationData = 'citation_data' in source;

  if (!hasCitationData) {
    return (
      <span data-citation-url={source.url} data-copy-exclude>
        {iconElement}
      </span>
    );
  }

  const overrideContent = renderCitationContentOverride?.({
    source: source as CitationSource,
    onRemove,
    removeDisabled,
  });

  const content =
    overrideContent != null ? (
      overrideContent
    ) : (
      <DefaultInlineContent
        source={source as CitationSource}
        onRemove={onRemove}
        removeDisabled={removeDisabled}
      />
    );

  return (
    <span data-citation-url={source.url} data-copy-exclude>
      <CitationPopover content={content} width={360}>
        {iconElement}
      </CitationPopover>
    </span>
  );
}
